import React, { useState, useEffect } from 'react';
import { booksAPI, customersAPI, settingsAPI } from '../services/api';
import { searchCustomers } from '../utils/searchHelpers';
import { getTodayInIsrael, formatDateInIsrael } from '../utils/dateUtils';
import ReturnBookModal from './ReturnBookModal';

function BookModal({ book: initialBook, notes, loans, onClose, onUpdate, onDeleted }) {
  const [book, setBook] = useState(initialBook);
  const [newNote, setNewNote] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showBorrowDialog, setShowBorrowDialog] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [settings, setSettings] = useState(null);
  const [isEditingBook, setIsEditingBook] = useState(false);
  const [editedBook, setEditedBook] = useState(null);

  const [borrowData, setBorrowData] = useState({
    customer: null,
    returnDate: getDefaultReturnDate(false),
    books: [{ id: book.id, title: book.book_name || book.title }]
  });

  // Extend loan state
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendingLoan, setExtendingLoan] = useState(null);
  const [newReturnDate, setNewReturnDate] = useState('');
  
  // Track loans state for updates
  const [booksLoans, setBooksLoans] = useState(loans || []);

  function getDefaultReturnDate(isNew) {
    const today = new Date(getTodayInIsrael());
    const days = isNew ? 7 : (settings?.default_loan_days?.value || 14);
    today.setDate(today.getDate() + days);
    return today.toISOString().split('T')[0];
  }

  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('📚 BookModal received new book data');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('initialBook:', initialBook);
    console.log('book.id:', initialBook?.id);
    console.log('book.condition (current):', initialBook?.condition);
    console.log('═══════════════════════════════════════════════════════════');
    setBook(initialBook);
  }, [initialBook]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await settingsAPI.getAll();
        // Extract just the values from the nested object
        const settingsValues = {};
        Object.entries(res.data || {}).forEach(([key, setting]) => {
          settingsValues[key] = typeof setting === 'object' ? setting.value : setting;
        });
        if (mounted) setSettings(settingsValues);
      } catch (err) {
        console.error('Error loading settings in BookModal:', err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Update loans when loans prop changes
  useEffect(() => {
    console.log('📚 Loans prop updated:', loans?.length || 0, 'loans');
    setBooksLoans(loans || []);
  }, [loans]);

  const handleSelectCustomer = (customer) => {
    setBorrowData(prev => ({
      ...prev,
      customer: customer,
      returnDate: getDefaultReturnDate(book.is_new || false)
    }));
    setCustomerSearch('');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowBorrowDialog(true);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      setLoading(true);
      await booksAPI.addNote(book.id, newNote);
      setNewNote('');
      setShowAddNote(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding note:', error);
      alert('שגיאה בהוספת הערה');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (noteId, noteText) => {
    setEditingNoteId(noteId);
    setEditingNoteText(noteText);
  };

  const handleSaveEditNote = async () => {
    if (!editingNoteText.trim()) return;
    try {
      setLoading(true);
      await booksAPI.updateNote(editingNoteId, editingNoteText);
      setEditingNoteId(null);
      setEditingNoteText('');
      onUpdate();
    } catch (error) {
      console.error('Error updating note:', error);
      alert('שגיאה בעדכון הערה');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק הערה זו?')) {
      try {
        setLoading(true);
        await booksAPI.deleteNote(noteId);
        onUpdate();
      } catch (error) {
        console.error('Error deleting note:', error);
        alert('שגיאה במחיקת הערה');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearchCustomer = async (searchTerm) => {
    setCustomerSearch(searchTerm);
    if (searchTerm.trim().length < 1) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = await searchCustomers(searchTerm);
    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleConfirmBorrow = async () => {
    if (!borrowData.customer) return;
    try {
      setLoading(true);
      console.log('🔄 Borrowing book for customer:', borrowData.customer.name);
      // ה-backend יחשב את return_date בעצמו בהתאם לis_new של הספר
      await customersAPI.borrowBook(borrowData.customer.identity_id, {
        book_id: book.id,
        book_condition: book.condition || 'good'
      });
      console.log('✅ Book borrowed successfully');
      setShowBorrowDialog(false);
      setBorrowData({
        customer: null,
        returnDate: getDefaultReturnDate(false),
        books: [{ id: book.id, title: book.book_name || book.title }]
      });
      onUpdate();
    } catch (error) {
      console.error('Error borrowing book:', error);
      if (error.response?.status === 400) {
        alert(error.response.data?.message || 'שגיאה בהשאלת הספר - בדוק את תנאי המנוי');
      } else {
        alert('שגיאה בהשאלת הספר');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async () => {
    // This is not used anymore - we use ReturnBookModal now
  };

  const handleReturnBook = (loan) => {
    setSelectedLoan(loan);
    setShowReturnModal(true);
  };

  const handleReturnSuccess = async () => {
    console.log('🔄 handleReturnSuccess called');
    setShowReturnModal(false);
    setSelectedLoan(null);
    // רענן את נתוני הספר מה-backend כדי לקבל את המצב המעודכן
    try {
      console.log('📥 Fetching book data for ID:', book.id);
      const response = await booksAPI.getById(book.id);
      console.log('📦 Got response:', response.data);
      // ה-API מחזיר { book, notes, loans }
      const bookData = response.data.book || response.data;
      console.log('📖 Book data:', bookData);
      console.log('✏️ Condition:', bookData.condition);
      setBook(bookData);
      console.log('✅ Book state updated');
    } catch (err) {
      console.error('❌ Error refreshing book data:', err);
    }
    onUpdate();
  };

  const handleExtendLoan = (loan) => {
    setExtendingLoan(loan);
    console.log('🔍 handleExtendLoan loan object:', loan);
    console.log('📅 return_date value:', loan.return_date);
    
    // Set default: 7 days for new books, 14 days for regular books
    const extensionDays = book.is_new ? 7 : 14;
    console.log('📅 Extension period:', extensionDays, 'days (is_new:', book.is_new, ')');
    let baseDate = null;
    
    try {
      if (loan.return_date) {
        // Use return_date if available - it's already in ISO format
        baseDate = new Date(loan.return_date);
      } else if (loan.loan_date) {
        // If return_date is null, use loan_date as base - it's also in ISO format
        console.log('⚠️ return_date is null, using loan_date as base:', loan.loan_date);
        baseDate = new Date(loan.loan_date);
      } else {
        // Last resort: use today
        console.log('⚠️ Both return_date and loan_date are null, using today');
        baseDate = new Date();
      }
      
      if (!baseDate || isNaN(baseDate.getTime())) {
        console.error('❌ Invalid date parsed:', { return_date: loan.return_date, loan_date: loan.loan_date });
        baseDate = new Date();
      }
      
      const defaultNewDate = new Date(baseDate);
      defaultNewDate.setDate(defaultNewDate.getDate() + extensionDays);
      
      const formattedDate = defaultNewDate.toISOString().split('T')[0];
      console.log('📅 Extend loan - Base date:', baseDate.toISOString(), 'New default:', formattedDate);
      setNewReturnDate(formattedDate);
      setShowExtendDialog(true);
    } catch (error) {
      console.error('❌ Error in handleExtendLoan:', error);
      alert('שגיאה בפתיחת חלונית ההארכה');
    }
  };

  const handleConfirmExtend = async () => {
    if (!extendingLoan || !newReturnDate) return;
    try {
      setLoading(true);
      console.log('🔄 Extending loan:', { loanId: extendingLoan.id, newReturnDate });
      await customersAPI.extendLoan(extendingLoan.customer_identity_id, extendingLoan.id, {
        loan_id: extendingLoan.id,
        new_return_date: newReturnDate
      });
      setShowExtendDialog(false);
      setExtendingLoan(null);
      setNewReturnDate('');
      console.log('✅ Loan extended, refreshing book data...');
      
      // Refresh book data to get updated loans
      try {
        const response = await booksAPI.getById(book.id);
        const bookData = response.data.book || response.data;
        const loansData = response.data.loans || [];
        console.log('📖 Book data updated:', bookData);
        console.log('📊 Loans updated:', loansData);
        setBook(bookData);
        // Note: Parent component should handle updating loans via onUpdate callback
      } catch (err) {
        console.error('❌ Error refreshing book data:', err);
      }
      
      onUpdate();
      alert('הלוואה הורחקה בהצלחה');
    } catch (error) {
      console.error('Error extending loan:', error);
      alert('שגיאה בהארכת ההלוואה');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelExtend = () => {
    setShowExtendDialog(false);
    setExtendingLoan(null);
    setNewReturnDate('');
  };

  const handleDelete = async () => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את הספר הזה?')) {
      try {
        setLoading(true);
        await booksAPI.delete(book.id);
        onDeleted();
      } catch (error) {
        console.error('Error deleting book:', error);
        alert('שגיאה במחיקת הספר');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditBook = () => {
    setEditedBook({
      condition: book.condition,
      is_new: book.is_new || false,
      notes: book.notes || ''
    });
    setIsEditingBook(true);
  };

  const handleSaveBookEdit = async () => {
    try {
      setLoading(true);
      await booksAPI.update(book.id, editedBook);
      setBook(prev => ({ ...prev, ...editedBook }));
      setIsEditingBook(false);
      setEditedBook(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating book:', error);
      alert('שגיאה בעדכון פרטי הספר');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBookEdit = () => {
    setIsEditingBook(false);
    setEditedBook(null);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{book.book_name || book.title}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Book Details */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>פרטי הספר</h3>
            {!isEditingBook && (
              <button className="btn btn-primary btn-sm" onClick={handleEditBook}>
                ✏️ עריכה
              </button>
            )}
          </div>
          
          {isEditingBook && editedBook ? (
            <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}><strong>מצב הספר:</strong></label>
                <select 
                  value={editedBook.condition} 
                  onChange={(e) => setEditedBook(prev => ({ ...prev, condition: e.target.value }))}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="חדש">חדש</option>
                  <option value="טוב">טוב</option>
                  <option value="משומש">משומש</option>
                  <option value="בלוי">בלוי</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input 
                    type="checkbox" 
                    checked={editedBook.is_new} 
                    onChange={(e) => setEditedBook(prev => ({ ...prev, is_new: e.target.checked }))}
                  />
                  <strong>האם חדש?</strong>
                </label>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}><strong>הערות:</strong></label>
                <textarea 
                  value={editedBook.notes} 
                  onChange={(e) => setEditedBook(prev => ({ ...prev, notes: e.target.value }))}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '100px' }}
                  placeholder="הוסף הערות על הספר..."
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn btn-success" 
                  onClick={handleSaveBookEdit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ✓ שמור
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleCancelBookEdit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ביטול
                </button>
              </div>
            </div>
          ) : (
            <>
              <p><strong>ברקוד:</strong> {book.barcode || '-'}</p>
              <p><strong>מחבר:</strong> {book.author}</p>
              <p><strong>מחלקה:</strong> {book.department_name || '-'}</p>
              <p><strong>קטגוריה:</strong> {book.category_name || '-'}</p>
              <p><strong>מצב הספר:</strong> {book.condition || '-'}</p>
              <p><strong>זמינות:</strong> <span className={`badge badge-${book.status_text?.includes('זמין') ? 'available' : 'borrowed'}`}>
                {book.status_text || 'זמין'}
              </span></p>
              <p><strong>בעלים:</strong> {book.owner || '-'}</p>
              <p><strong>האם חדש?:</strong> {book.is_new ? '✓ כן' : '❌ לא'}</p>
              <p><strong>הערות:</strong> {book.notes || '-'}</p>
            </>
          )}
        </div>

        {/* Borrow/Return Actions */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
          <h3>השאל ספרים</h3>
          {book.status_text && book.status_text.includes('זמין') && !book.status_text.includes('לא') ? (
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="חפש לקוח (שם, טלפון, אימייל או ID)"
                value={customerSearch}
                onChange={(e) => handleSearchCustomer(e.target.value)}
                onFocus={() => customerSearch && setShowSearchResults(true)}
                style={{ width: '100%' }}
              />
              {showSearchResults && searchResults.length > 0 && (
                <div className="search-results-dropdown">
                  {searchResults.map((customer) => (
                    <div
                      key={customer.id}
                      className="search-result-item"
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <div><strong>{customer.name}</strong></div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        {customer.phone && <span>📞 {customer.phone} | </span>}
                        {customer.email && <span>📧 {customer.email}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showSearchResults && customerSearch && searchResults.length === 0 && (
                <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>
                  לא נמצאו לקוחות
                </div>
              )}
            </div>
          ) : (
            <button className="btn btn-warning" onClick={handleReturn} disabled={loading}>
              📥 החזר
            </button>
          )}
        </div>

        {/* Borrow Dialog */}
        {showBorrowDialog && borrowData.customer && (
          <div className="modal-overlay" style={{ position: 'fixed' }}>
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h3>אישור השאלה</h3>
                <button className="close-btn" onClick={() => setShowBorrowDialog(false)}>✕</button>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <h4>לקוח</h4>
                <p><strong>{borrowData.customer.name}</strong></p>
                <p style={{ color: '#666', fontSize: '0.9rem' }}>
                  📞 {borrowData.customer.phone} | 📧 {borrowData.customer.email}
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4>תאריך החזרה צפוי</h4>
                <input
                  type="date"
                  value={borrowData.returnDate}
                  onChange={(e) => setBorrowData(prev => ({ ...prev, returnDate: e.target.value }))}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4>ספרים להשאלה</h4>
                <div style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                  {borrowData.books.map((b, idx) => (
                    <div key={idx} style={{ 
                      padding: '8px', 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      borderBottom: idx < borrowData.books.length - 1 ? '1px solid #eee' : 'none' 
                    }}>
                      <span>{b.title}</span>
                      {borrowData.books.length > 1 && (
                        <button 
                          className="btn btn-sm" 
                          style={{ backgroundColor: '#ff4444', color: 'white', padding: '4px 8px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                          onClick={() => setBorrowData(prev => ({
                            ...prev,
                            books: prev.books.filter((_, i) => i !== idx)
                          }))}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button 
                  className="btn btn-sm"
                  style={{ width: '100%', padding: '8px', backgroundColor: '#44aa44', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={() => {
                    // בעתיד - נוסיף אפשרות לבחור ספרים נוספים
                    alert('הוספת ספרים נוספים - בקרוב');
                  }}
                >
                  + הוסף ספר נוסף
                </button>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn btn-success" 
                  onClick={handleConfirmBorrow}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ✓ אשר השאלה
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowBorrowDialog(false)}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Borrow History */}
        {booksLoans && booksLoans.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3>היסטוריית השאלות</h3>
            <table className="data-table" style={{ marginTop: '10px' }}>
              <thead>
                <tr>
                  <th>לקוח</th>
                  <th>תאריך השאלה</th>
                  <th>תאריך החזרה</th>
                  <th>סטטוס</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {booksLoans.map((loan, index) => (
                  <tr key={index}>
                    <td>{loan.customer_name}</td>
                    <td>{formatDateInIsrael(loan.loan_date)}</td>
                    <td>{loan.return_date ? formatDateInIsrael(loan.return_date) : '-'}</td>
                    <td>
                      <span className={`badge badge-${loan.status}`}>
                        {loan.status === 'borrowed' ? '❌ מושאל' : '✓ הוחזר'}
                      </span>
                    </td>
                    <td>
                      {loan.status === 'borrowed' && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleReturnBook(loan)}
                            disabled={loading}
                          >
                            📥 החזר
                          </button>
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => handleExtendLoan(loan)}
                            disabled={loading}
                            title="הארך את תאריך ההחזרה"
                          >
                            ⏱️ הארך
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3>הערות על הספר</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowAddNote(!showAddNote)}
              disabled={editingNoteId !== null}
            >
              {showAddNote ? 'ביטול' : '➕ הוסף הערה'}
            </button>
          </div>

          {showAddNote && (
            <div style={{ marginBottom: '10px' }}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="הוסף הערה על מצב הספר..."
                style={{ marginBottom: '10px' }}
              />
              <button
                className="btn btn-success"
                onClick={handleAddNote}
                disabled={loading}
              >
                שמור הערה
              </button>
            </div>
          )}

          {notes && notes.length > 0 ? (
            <div>
              {notes.map((note) => (
                <div
                  key={note.id}
                  style={{
                    padding: '10px',
                    backgroundColor: editingNoteId === note.id ? '#fff3cd' : '#f9f9f9',
                    borderRight: '3px solid #667eea',
                    marginBottom: '10px',
                    borderRadius: '3px'
                  }}
                >
                  {editingNoteId === note.id ? (
                    <div>
                      <textarea
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        style={{ marginBottom: '10px', width: '100%', minHeight: '80px' }}
                      />
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={handleSaveEditNote}
                          disabled={loading}
                        >
                          ✓ שמור
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleCancelEditNote}
                          disabled={loading}
                        >
                          ביטול
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p style={{ margin: '5px 0', whiteSpace: 'pre-wrap' }}>{note.note}</p>
                      <small style={{ color: '#999' }}>
                        {formatDateInIsrael(note.created_at)}
                      </small>
                      <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => handleEditNote(note.id, note.note)}
                          disabled={loading}
                        >
                          ✏️ ערוך
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={loading}
                        >
                          🗑️ מחק
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#999' }}>אין הערות</p>
          )}
        </div>

        {/* Return Book Modal */}
        {showReturnModal && selectedLoan && (
          <ReturnBookModal
            book={{
              id: book.id,
              book_id: book.id,
              book_title: selectedLoan.book_title || book.book_name || book.title,
              title: selectedLoan.book_title || book.book_name || book.title
            }}
            loan={selectedLoan}
            customer={{ identity_id: selectedLoan.customer_identity_id, name: selectedLoan.customer_name }}
            onClose={() => {
              setShowReturnModal(false);
              setSelectedLoan(null);
            }}
            onSuccess={handleReturnSuccess}
          />
        )}

        {/* Extend Loan Dialog */}
        {showExtendDialog && extendingLoan && (
          <div className="modal-overlay" style={{ position: 'fixed' }}>
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h3>הארכת הלוואה</h3>
                <button className="close-btn" onClick={handleCancelExtend}>✕</button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4>פרטי ההלוואה</h4>
                {(() => {
                  console.log('🔍 BookModal extendingLoan data:', extendingLoan);
                  console.log('📅 BookModal return_date value:', extendingLoan?.return_date);
                  return null;
                })()}
                <p><strong>לקוח:</strong> {extendingLoan.customer_name}</p>
                <p><strong>ספר:</strong> {extendingLoan.book_title}</p>
                <p><strong>תאריך השאלה:</strong> {formatDateInIsrael(extendingLoan.loan_date)}</p>
                <p>
                  <strong>תאריך החזרה הנוכחי:</strong>{' '}
                  {extendingLoan.return_date 
                    ? formatDateInIsrael(extendingLoan.return_date)
                    : extendingLoan.loan_date 
                    ? `${formatDateInIsrael(extendingLoan.loan_date)} (מבוסס על תאריך השאלה)`
                    : 'לא קבוע'
                  }
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4>תאריך החזרה חדש</h4>
                <input
                  type="date"
                  value={newReturnDate}
                  onChange={(e) => setNewReturnDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn btn-success" 
                  onClick={handleConfirmExtend}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ✓ הארך הלוואה
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleCancelExtend}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Button */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-danger" onClick={handleDelete} disabled={loading}>
            🗑️ מחק ספר
          </button>
          <button className="btn btn-primary" onClick={onClose}>
            סגור
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookModal;
